import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  output,
} from '@angular/core';

import {
  Icon,
  TableBuilderFactory,
  TableComponent,
  TableConfig,
  TableLazyLoad,
} from '@web/shared/ui';
import { AdminDictionaryListAction } from '../../enums/admin-dictionary-list-action';
import { AdminDictionaryListItemAction } from '../../enums/admin-dictionary-list-item-action';
import { Route, RouteParam } from '@web/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { of } from 'rxjs';
import { AdminDictionaryCountryCollectionItem } from '../../models/admin-dictionary-country-collection-item';
import { AdminDictionaryCountryCollectionFilter } from '../../models/admin-dictionary-country-collection-filter';
import { AdminDictionaryCountryDataSourceService } from '../../services/admin-dictionary-country-data-source.service';

@Component({
  selector: 'app-admin-dictionary-country-list',
  imports: [TableComponent],
  templateUrl: './admin-dictionary-country-list.component.html',
  styleUrl: './admin-dictionary-country-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminDictionaryCountryListComponent {
  /* prettier-ignore */
  private readonly tableBuilder = inject(TableBuilderFactory<AdminDictionaryCountryCollectionItem, AdminDictionaryCountryCollectionFilter, AdminDictionaryListItemAction, never, AdminDictionaryListAction>).create();
  /* prettier-ignore */
  private readonly dictionaryDataSource = inject(AdminDictionaryCountryDataSourceService);

  public readonly createCountryVisibility = input<boolean>(false);
  public readonly removeSuccessEvent = output<void>();

  protected tableConfig = this.buildTableConfig();

  private readonly destroyRef = inject(DestroyRef);

  private buildTableConfig(): TableConfig<
    AdminDictionaryCountryCollectionItem,
    AdminDictionaryCountryCollectionFilter,
    AdminDictionaryListItemAction,
    never,
    AdminDictionaryListAction
  > {
    return this.tableBuilder
      .columns((builder) => [
        builder
          .initText('label')
          .filterKey('label')
          .filterable(true)
          .width('25rem')
          .label('Etykieta')
          .build(),

        builder //
          .initText('isoCode')
          .filterKey('isoCode')
          .filterable(true)
          .width('25rem')
          .label('Kod kraju (ISO)')
          .build(),

        builder
          .initDate('createdAt')
          .filterKey('label')
          .filterable(true)
          .label('Data dodania')
          .build(),
      ])
      .filters((builder) => [
        builder.initText('label').build(),
        builder.initText('isoCode').build(),
        builder.initDateRange('createdAt').build(),
      ])
      .itemActions((builder) => [
        builder
          .initRouterLink(AdminDictionaryListItemAction.Edit)
          .visibility((model) => model.canBeEdited)
          .routerLink(
            (provider) => (model) =>
              provider.routerLink(Route.DictionaryCountryEdit, {
                [RouteParam.Guid]: model.id,
              }),
          )
          .tooltip('Edycja')
          .icon(Icon.Pencil)
          .build(),

        builder
          .initCommand(AdminDictionaryListItemAction.Remove)
          .visibility((model) => model.canBeRemoved)
          .command((item) => this.confirmAndRemove(item))
          .tooltip('Usuń')
          .icon(Icon.Trash)
          .build(),
      ])
      .actions((builder) => [
        builder
          .initRouterLink(AdminDictionaryListAction.Create)
          .routerLink((provider) =>
            provider.routerLink(Route.DictionaryCountryCreate),
          )
          .visibility(() => of(this.createCountryVisibility()))
          .label('Dodaj kraj')
          .build(),
      ])
      .dataKey('id')
      .dataSource({
        fetch: (
          lazyLoad: TableLazyLoad<AdminDictionaryCountryCollectionFilter>,
        ) => this.dictionaryDataSource.fetchCollection(lazyLoad),
      })
      .build();
  }

  private confirmAndRemove(item: AdminDictionaryCountryCollectionItem): void {
    this.dictionaryDataSource
      .confirmAndRemoveById(item.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.removeSuccessEvent.emit());
  }
}
